/**
 * Frame layout for the comparison video, resolved straight to pixel rects.
 *
 * This feature draws its own frames on a 2D canvas and encodes them itself, so
 * there is no editor coordinate model to satisfy — a rect is just a rect. The
 * same resolved layout drives the form preview, the playback preview and the
 * export, so all three are guaranteed to match.
 */
import type { AspectId, WatermarkCorner } from "./types";

export interface Rect {
	x: number;
	y: number;
	w: number;
	h: number;
}

export interface CanvasSize {
	width: number;
	height: number;
}

export interface ResolvedLayout {
	canvas: CanvasSize;
	/** Bordered white card for each option. */
	thisCard: Rect;
	thatCard: Rect;
	/** Inset from the card edge to the image, in px. */
	cardPadding: number;
	cardRadius: number;
	cardBorder: number;
	/** Label chip: vertical position of its top edge, and its type size. */
	labelTop: number;
	labelFontPx: number;
	character: Rect;
	/** Captions sit this far above the bottom edge. */
	captionBottom: number;
	captionFontPx: number;
	captionMaxWidth: number;
	/**
	 * Largest box the watermark may occupy, plus the margin from the frame edge.
	 * The inset is a single value so the gap looks the same across and down —
	 * a percentage of width and one of height read as lopsided on a 9:16 frame.
	 */
	watermark: { w: number; h: number; inset: number };
	watermarkFontPx: number;
}

/** Proportions, as fractions of the canvas. Tuned per aspect. */
interface LayoutSpec {
	canvas: CanvasSize;
	cardCx: [number, number];
	cardCy: number;
	cardW: number;
	cardH: number;
	labelTop: number;
	labelFont: number;
	characterCy: number;
	characterW: number;
	characterH: number;
	captionBottom: number;
	captionFont: number;
	/** w/h are fractions of width/height; inset is a fraction of the short edge. */
	watermark: { w: number; h: number; inset: number };
	watermarkFont: number;
}

export const ASPECTS: Array<{ id: AspectId; label: string; hint: string }> = [
	{ id: "9:16", label: "9:16", hint: "Reels, Shorts, TikTok" },
	{ id: "1:1", label: "1:1", hint: "Feed posts" },
	{ id: "16:9", label: "16:9", hint: "YouTube, landing pages" },
];

const SPECS: Record<AspectId, LayoutSpec> = {
	"9:16": {
		canvas: { width: 1080, height: 1920 },
		cardCx: [0.265, 0.735],
		cardCy: 0.175,
		cardW: 0.4,
		cardH: 0.225,
		labelTop: 0.305,
		labelFont: 0.028,
		characterCy: 0.615,
		characterW: 0.72,
		characterH: 0.5,
		captionBottom: 0.07,
		captionFont: 0.034,
		watermark: { w: 0.26, h: 0.042, inset: 0.045 },
		watermarkFont: 0.018,
	},
	"1:1": {
		canvas: { width: 1080, height: 1080 },
		cardCx: [0.265, 0.735],
		cardCy: 0.21,
		cardW: 0.4,
		cardH: 0.27,
		labelTop: 0.375,
		labelFont: 0.026,
		characterCy: 0.645,
		characterW: 0.44,
		characterH: 0.4,
		captionBottom: 0.07,
		captionFont: 0.032,
		watermark: { w: 0.24, h: 0.05, inset: 0.045 },
		watermarkFont: 0.022,
	},
	"16:9": {
		canvas: { width: 1920, height: 1080 },
		cardCx: [0.17, 0.83],
		cardCy: 0.3,
		cardW: 0.24,
		cardH: 0.34,
		labelTop: 0.5,
		labelFont: 0.024,
		characterCy: 0.54,
		characterW: 0.24,
		characterH: 0.62,
		captionBottom: 0.06,
		captionFont: 0.03,
		watermark: { w: 0.15, h: 0.055, inset: 0.045 },
		watermarkFont: 0.02,
	},
};

function centeredRect({
	canvas,
	cx,
	cy,
	w,
	h,
}: {
	canvas: CanvasSize;
	cx: number;
	cy: number;
	w: number;
	h: number;
}): Rect {
	const width = w * canvas.width;
	const height = h * canvas.height;
	return {
		x: cx * canvas.width - width / 2,
		y: cy * canvas.height - height / 2,
		w: width,
		h: height,
	};
}

export function getLayout({ aspect }: { aspect: AspectId }): ResolvedLayout {
	const spec = SPECS[aspect] ?? SPECS["9:16"];
	const canvas = spec.canvas;
	const card = (cx: number) =>
		centeredRect({ canvas, cx, cy: spec.cardCy, w: spec.cardW, h: spec.cardH });
	const thisCard = card(spec.cardCx[0]);

	return {
		canvas,
		thisCard,
		thatCard: card(spec.cardCx[1]),
		cardPadding: Math.round(Math.min(thisCard.w, thisCard.h) * 0.13),
		cardRadius: Math.round(Math.min(thisCard.w, thisCard.h) * 0.09),
		cardBorder: Math.max(2, Math.round(canvas.width * 0.0028)),
		labelTop: spec.labelTop * canvas.height,
		labelFontPx: spec.labelFont * canvas.height,
		character: centeredRect({
			canvas,
			cx: 0.5,
			cy: spec.characterCy,
			w: spec.characterW,
			h: spec.characterH,
		}),
		captionBottom: spec.captionBottom * canvas.height,
		captionFontPx: spec.captionFont * canvas.height,
		captionMaxWidth: canvas.width * 0.86,
		watermark: {
			w: spec.watermark.w * canvas.width,
			h: spec.watermark.h * canvas.height,
			inset: spec.watermark.inset * Math.min(canvas.width, canvas.height),
		},
		watermarkFontPx: spec.watermarkFont * canvas.height,
	};
}

export function watermarkRect({
	layout,
	corner,
}: {
	layout: ResolvedLayout;
	corner: WatermarkCorner;
}): Rect {
	const { w, h, inset } = layout.watermark;
	const { width, height } = layout.canvas;
	const isLeft = corner === "top-left" || corner === "bottom-left";
	const isTop = corner === "top-left" || corner === "top-right";

	return {
		x: isLeft ? inset : width - inset - w,
		y: isTop ? inset : height - inset - h,
		w,
		h,
	};
}

/** Contain-fit a source inside a rect, preserving aspect. */
export function containRect({
	source,
	within,
}: {
	source: { width: number; height: number };
	within: Rect;
}): Rect {
	const scale = Math.min(
		within.w / Math.max(1, source.width),
		within.h / Math.max(1, source.height),
	);
	const w = source.width * scale;
	const h = source.height * scale;
	return {
		x: within.x + (within.w - w) / 2,
		y: within.y + (within.h - h) / 2,
		w,
		h,
	};
}

/** Cover-fill a rect with a source, cropping the overflow. */
export function coverRect({
	source,
	within,
}: {
	source: { width: number; height: number };
	within: Rect;
}): Rect {
	const scale = Math.max(
		within.w / Math.max(1, source.width),
		within.h / Math.max(1, source.height),
	);
	const w = source.width * scale;
	const h = source.height * scale;
	return {
		x: within.x + (within.w - w) / 2,
		y: within.y + (within.h - h) / 2,
		w,
		h,
	};
}
